/**
 * shared/weapons/defs.ts — typed WeaponDef built from the generated raw weapon files.
 *
 * OWNER: weapons agent (may add fields; keep existing names). Contract shape by integration.
 * Data: weapons.generated.ts (tools/gen-weapon-defs.mjs <- research/data/weapons_mp.json).
 * Times: the weapon file stores seconds; the engine uses integer ms (T5 bg_weapons_load_obj
 * type 8 = ms) [research/weapons-mp.md §0]. All *Ms fields here are Math.round(s * 1000).
 */
import { SURFACE_TYPES } from '../collision/types';
import { NUM_WEAPONS, WEAPON_NAMES, WeaponId } from '../types';
import { WEAPON_FILES, type WeaponFileRaw } from './weapons.generated';

export type WeaponType = 'projectile' | 'grenade' | 'melee' | 'bullet' | string;
export type Stickiness = 'Don\'t stick' | 'Stick to all' | 'Stick to flesh' | string;

export interface WeaponDef {
  id: WeaponId;
  name: string; //               'crossbow_mp'
  displayNameEnglish: string;
  weaponType: WeaponType;
  weaponClass: string;
  inventoryType: string; //      primary | offhand | melee | item
  fireType: string;
  offhandClass: string;
  playerAnimType: string;
  // ammo
  clipSize: number;
  maxAmmo: number;
  startAmmo: number;
  clipOnly: boolean;
  // damage
  damage: number;
  playerDamage: number;
  minDamage: number;
  explosionRadius: number;
  explosionInnerDamage: number;
  explosionOuterDamage: number;
  damageConeAngle: number;
  /** Hit-location multipliers (locHead, locTorsoUpper, ...). */
  locationMultipliers: Readonly<Record<string, number>>;
  // melee
  meleeDamage: number;
  meleeTimeMs: number;
  meleeDelayMs: number;
  meleeChargeTimeMs: number;
  meleeChargeDelayMs: number;
  meleeChargeRange: number;
  useAsMelee: boolean;
  // timing (ms)
  fireTimeMs: number;
  fireDelayMs: number;
  lastFireTimeMs: number;
  holdFireTimeMs: number;
  rechamberTimeMs: number;
  reloadTimeMs: number;
  reloadEmptyTimeMs: number;
  reloadAddTimeMs: number;
  reloadShowRocketTimeMs: number;
  raiseTimeMs: number;
  dropTimeMs: number;
  firstRaiseTimeMs: number;
  quickRaiseTimeMs: number;
  quickDropTimeMs: number;
  emptyRaiseTimeMs: number;
  emptyDropTimeMs: number;
  sprintInTimeMs: number;
  sprintLoopTimeMs: number;
  sprintOutTimeMs: number;
  dtpInTimeMs: number;
  dtpLoopTimeMs: number;
  dtpOutTimeMs: number;
  adsTransInTimeMs: number;
  adsTransOutTimeMs: number;
  fuseTimeMs: number;
  noQuickDropWhenEmpty: boolean;
  // ADS / movement
  aimDownSight: boolean;
  adsZoomFov: number;
  adsZoomInFrac: number;
  adsZoomOutFrac: number;
  adsMoveSpeedScale: number;
  moveSpeedScale: number;
  sprintDurationScale: number;
  // spread (degrees)
  hipSpreadStandMin: number;
  hipSpreadDuckedMin: number;
  hipSpreadProneMin: number;
  hipSpreadMax: number;
  hipSpreadDuckedMax: number;
  hipSpreadProneMax: number;
  hipSpreadDecayRate: number;
  hipSpreadFireAdd: number;
  hipSpreadMoveAdd: number;
  adsSpread: number;
  // projectile
  projectileSpeed: number;
  projectileSpeedUp: number;
  projectileSpeedForward: number;
  projectileSpeedRelativeUp: number;
  projectileLifetimeMs: number;
  guidedMissileType: string; //  'Ballistic' -> gravity
  stickiness: Stickiness;
  rotateType: string;
  grenadeWeapon: WeaponId; //    crossbow -> EXPLOSIVE_BOLT
  timedDetonation: boolean;
  retrievable: boolean;
  dieOnRespawn: boolean;
  offhandHoldIsCancelable: boolean;
  useAntiLagRewind: boolean;
  /** Bounce factors indexed by surfaceType (SURFACE_TYPES), Default used where the file has none. */
  parallelBounce: readonly number[];
  perpendicularBounce: readonly number[];
  // aim assist [research/controller.md §5]
  aimAssistRange: number;
  aimAssistRangeAds: number;
  autoAimRange: number;
  enemyCrosshairRange: number;
  lockOnRadius: number;
  lockOnSpeed: number;
  // ---- added by the weapons agent (PM_Weapon / missiles need them) -----------------------------
  /** T5 iPositionReloadTransTime: ADS is refused during a reload until weaponTime <= this [WPN §2.3 adsReloadTransTime]. */
  adsReloadTransTimeMs: number;
  reloadWhileAds: boolean;
  noAdsWhenMagEmpty: boolean;
  noADSAutoReload: boolean;
  /** 'none' or a scope overlay (T5: melee is blocked while ADS with an overlay reticle). */
  adsOverlayReticle: string;
  hipSpreadDuckedDecay: number;
  hipSpreadProneDecay: number;
  hipSpreadTurnAdd: number;
  /** Single Shot (T5 fireType != FULLAUTO -> ShotLimitReached needs a trigger re-press). */
  singleShot: boolean;
  holdButtonToThrow: boolean;
  /** 'None' | 'Lethal grenade' | ... (T5 BG_IsQuickSwitch: any offhand slot -> quick). */
  offhandSlot: string;
  /** In-flight rotation enabled (T5 G_FireGrenade `rotate`) and its style. */
  rotate: boolean;
  /** 'bolt' (bolt/knife: angles follow velocity), 'grenade', 'none' [WPN §2.1 projExplosionType]. */
  projExplosionType: string;
  impactType: string;
  bounceSound: string;
  /** Rolling grenades use T5 GrenadeBounceVelocity's rolling branch (0 for every SnS weapon). */
  isRollingGrenade: boolean;
  freezeMovementWhenFiring: boolean;
  /** Every raw field (models, anims, sounds, effects, viewmodel pose, sway, kick...). */
  raw: WeaponFileRaw;
}

function num(raw: WeaponFileRaw, k: string): number {
  const v = raw[k];
  return typeof v === 'number' ? v : 0;
}
function str(raw: WeaponFileRaw, k: string): string {
  const v = raw[k];
  return typeof v === 'string' ? v : v === undefined ? '' : String(v);
}
function ms(raw: WeaponFileRaw, k: string): number {
  return Math.round(num(raw, k) * 1000);
}
function bool(raw: WeaponFileRaw, k: string): boolean {
  return num(raw, k) !== 0;
}
function bounceName(surface: string): string {
  const special: Record<string, string> = { paintedmetal: 'PaintedMetal', riotshield: 'RiotShield', tallgrass: 'TallGrass' };
  return special[surface] ?? surface.charAt(0).toUpperCase() + surface.slice(1);
}
function bounceTable(raw: WeaponFileRaw, prefix: 'parallel' | 'perpendicular'): number[] {
  const def = num(raw, `${prefix}DefaultBounce`);
  return SURFACE_TYPES.map((s) => {
    const k = `${prefix}${bounceName(s)}Bounce`;
    return k in raw ? num(raw, k) : def;
  });
}

export function buildWeaponDef(id: WeaponId, raw: WeaponFileRaw): WeaponDef {
  const loc: Record<string, number> = {};
  for (const k of Object.keys(raw)) if (/^loc[A-Z]/.test(k)) loc[k] = num(raw, k);
  const lifetime = num(raw, 'projectileLifetime');
  return {
    id,
    name: WEAPON_NAMES[id],
    displayNameEnglish: str(raw, 'displayNameEnglish'),
    weaponType: str(raw, 'weaponType'),
    weaponClass: str(raw, 'weaponClass'),
    inventoryType: str(raw, 'inventoryType'),
    fireType: str(raw, 'fireType'),
    offhandClass: str(raw, 'offhandClass'),
    playerAnimType: str(raw, 'playerAnimType'),
    clipSize: num(raw, 'clipSize'),
    maxAmmo: num(raw, 'maxAmmo'),
    startAmmo: num(raw, 'startAmmo'),
    clipOnly: bool(raw, 'clipOnly'),
    damage: num(raw, 'damage'),
    playerDamage: num(raw, 'playerDamage'),
    minDamage: num(raw, 'minDamage'),
    explosionRadius: num(raw, 'explosionRadius'),
    explosionInnerDamage: num(raw, 'explosionInnerDamage'),
    explosionOuterDamage: num(raw, 'explosionOuterDamage'),
    damageConeAngle: num(raw, 'damageConeAngle'),
    locationMultipliers: loc,
    meleeDamage: num(raw, 'meleeDamage'),
    meleeTimeMs: ms(raw, 'meleeTime'),
    meleeDelayMs: ms(raw, 'meleeDelay'),
    meleeChargeTimeMs: ms(raw, 'meleeChargeTime'),
    meleeChargeDelayMs: ms(raw, 'meleeChargeDelay'),
    meleeChargeRange: num(raw, 'meleeChargeRange'),
    useAsMelee: bool(raw, 'useAsMelee'),
    fireTimeMs: ms(raw, 'fireTime'),
    fireDelayMs: ms(raw, 'fireDelay'),
    lastFireTimeMs: ms(raw, 'lastFireTime'),
    holdFireTimeMs: ms(raw, 'holdFireTime'),
    rechamberTimeMs: ms(raw, 'rechamberTime'),
    reloadTimeMs: ms(raw, 'reloadTime'),
    reloadEmptyTimeMs: ms(raw, 'reloadEmptyTime'),
    reloadAddTimeMs: ms(raw, 'reloadAddTime'),
    reloadShowRocketTimeMs: ms(raw, 'reloadShowRocketTime'),
    raiseTimeMs: ms(raw, 'raiseTime'),
    dropTimeMs: ms(raw, 'dropTime'),
    firstRaiseTimeMs: ms(raw, 'firstRaiseTime'),
    quickRaiseTimeMs: ms(raw, 'quickRaiseTime'),
    quickDropTimeMs: ms(raw, 'quickDropTime'),
    emptyRaiseTimeMs: ms(raw, 'emptyRaiseTime'),
    emptyDropTimeMs: ms(raw, 'emptyDropTime'),
    sprintInTimeMs: ms(raw, 'sprintInTime'),
    sprintLoopTimeMs: ms(raw, 'sprintLoopTime'),
    sprintOutTimeMs: ms(raw, 'sprintOutTime'),
    dtpInTimeMs: ms(raw, 'dtpInTime'),
    dtpLoopTimeMs: ms(raw, 'dtpLoopTime'),
    dtpOutTimeMs: ms(raw, 'dtpOutTime'),
    adsTransInTimeMs: ms(raw, 'adsTransInTime'),
    adsTransOutTimeMs: ms(raw, 'adsTransOutTime'),
    fuseTimeMs: ms(raw, 'fuseTime'),
    noQuickDropWhenEmpty: bool(raw, 'noQuickDropWhenEmpty'),
    aimDownSight: bool(raw, 'aimDownSight'),
    adsZoomFov: num(raw, 'adsZoomFov1'),
    adsZoomInFrac: num(raw, 'adsZoomInFrac'),
    adsZoomOutFrac: num(raw, 'adsZoomOutFrac'),
    adsMoveSpeedScale: num(raw, 'adsMoveSpeedScale'),
    moveSpeedScale: num(raw, 'moveSpeedScale'),
    sprintDurationScale: num(raw, 'sprintDurationScale'),
    hipSpreadStandMin: num(raw, 'hipSpreadStandMin'),
    hipSpreadDuckedMin: num(raw, 'hipSpreadDuckedMin'),
    hipSpreadProneMin: num(raw, 'hipSpreadProneMin'),
    hipSpreadMax: num(raw, 'hipSpreadMax'),
    hipSpreadDuckedMax: num(raw, 'hipSpreadDuckedMax'),
    hipSpreadProneMax: num(raw, 'hipSpreadProneMax'),
    hipSpreadDecayRate: num(raw, 'hipSpreadDecayRate'),
    hipSpreadFireAdd: num(raw, 'hipSpreadFireAdd'),
    hipSpreadMoveAdd: num(raw, 'hipSpreadMoveAdd'),
    adsSpread: num(raw, 'adsSpread'),
    projectileSpeed: num(raw, 'projectileSpeed'),
    projectileSpeedUp: num(raw, 'projectileSpeedUp'),
    projectileSpeedForward: num(raw, 'projectileSpeedForward'),
    projectileSpeedRelativeUp: num(raw, 'projectileSpeedRelativeUp'),
    projectileLifetimeMs: Math.round(lifetime * 1000),
    guidedMissileType: str(raw, 'guidedMissileType'),
    stickiness: str(raw, 'stickiness'),
    rotateType: str(raw, 'rotateType'),
    grenadeWeapon: Math.max(0, WEAPON_NAMES.indexOf(str(raw, 'grenadeWeapon'))) as WeaponId,
    timedDetonation: bool(raw, 'timedDetonation'),
    retrievable: bool(raw, 'retrievable'),
    dieOnRespawn: bool(raw, 'dieOnRespawn'),
    offhandHoldIsCancelable: bool(raw, 'offhandHoldIsCancelable'),
    useAntiLagRewind: bool(raw, 'useAntiLagRewind'),
    parallelBounce: bounceTable(raw, 'parallel'),
    perpendicularBounce: bounceTable(raw, 'perpendicular'),
    aimAssistRange: num(raw, 'aimAssistRange'),
    aimAssistRangeAds: num(raw, 'aimAssistRangeAds'),
    autoAimRange: num(raw, 'autoAimRange'),
    enemyCrosshairRange: num(raw, 'enemyCrosshairRange'),
    lockOnRadius: num(raw, 'lockOnRadius'),
    lockOnSpeed: num(raw, 'lockOnSpeed'),
    adsReloadTransTimeMs: ms(raw, 'adsReloadTransTime'),
    reloadWhileAds: bool(raw, 'reloadWhileAds'),
    noAdsWhenMagEmpty: bool(raw, 'noAdsWhenMagEmpty'),
    noADSAutoReload: bool(raw, 'noADSAutoReload'),
    adsOverlayReticle: str(raw, 'adsOverlayReticle') || 'none',
    hipSpreadDuckedDecay: num(raw, 'hipSpreadDuckedDecay'),
    hipSpreadProneDecay: num(raw, 'hipSpreadProneDecay'),
    hipSpreadTurnAdd: num(raw, 'hipSpreadTurnAdd'),
    singleShot: str(raw, 'fireType') === 'Single Shot',
    holdButtonToThrow: bool(raw, 'holdButtonToThrow'),
    offhandSlot: str(raw, 'offhandSlot') || 'None',
    rotate: bool(raw, 'rotate'),
    projExplosionType: str(raw, 'projExplosionType'),
    impactType: str(raw, 'impactType'),
    bounceSound: str(raw, 'bounceSound'),
    isRollingGrenade: bool(raw, 'isRollingGrenade'),
    freezeMovementWhenFiring: bool(raw, 'freezeMovementWhenFiring'),
    raw,
  };
}

/** All SnS weapon defs indexed by WeaponId (index 0 = NONE, an all-zero def). */
export const WEAPON_DEFS: readonly WeaponDef[] = (() => {
  const defs: WeaponDef[] = [];
  for (let id = 0; id < NUM_WEAPONS; id++) {
    const raw = WEAPON_FILES[WEAPON_NAMES[id]] ?? {};
    defs.push(buildWeaponDef(id as WeaponId, raw));
  }
  return defs;
})();

export function getWeaponDef(id: WeaponId): WeaponDef {
  return WEAPON_DEFS[id] ?? WEAPON_DEFS[0];
}
